/**
 * Utility functions for cleaning up wallet SDKs and storage
 * Prevents conflicts between EVM and Tron SDKs
 * 
 * Preserves important user/session data:
 * - cancelUrl
 * - refreshToken
 * - token
 * - user
 * - userToken
 */

// Fields to preserve during cleanup
const PRESERVED_FIELDS = ['cancelUrl', 'refreshToken', 'token', 'user', 'userToken'];

/**
 * Cleans up EVM/WalletConnect storage
 * Removes all wallet-related storage except preserved fields
 */
export const cleanupEvmStorage = () => {
  try {
    // Get all localStorage keys
    const keys = Object.keys(localStorage);

    // Remove wallet-related keys except preserved fields
    keys.forEach(key => {
      // Skip preserved fields
      if (PRESERVED_FIELDS.includes(key)) {
        return;
      }

      // Remove Wagmi/WalletConnect related keys
      if (
        key.startsWith("wagmi.") ||
        key.startsWith("@w3m/") ||
        key.startsWith("wc@2:") ||
        key.startsWith("WALLET_CONNECT") ||
        key.toLowerCase().includes("walletconnect") ||
        key.toLowerCase().includes("wagmi")
      ) {
        localStorage.removeItem(key);
      }
    });

    // IndexedDB cleanup
    if (typeof indexedDB !== 'undefined') {
      indexedDB.deleteDatabase("WALLET_CONNECT_V2_INDEXED_DB");
      indexedDB.deleteDatabase("W3M");
    }

    console.log("✅ EVM storage cleaned up");
  } catch (error) {
    console.error("Error cleaning EVM storage:", error);
  }
};

/**
 * Cleans up Tron storage
 * Removes all Tron-related storage except preserved fields
 */
export const cleanupTronStorage = () => {
  try {
    // Get all localStorage keys
    const keys = Object.keys(localStorage);

    // Remove Tron-related keys except preserved fields
    keys.forEach(key => {
      // Skip preserved fields
      if (PRESERVED_FIELDS.includes(key)) {
        return;
      }

      // Remove Tron-related keys
      if (
        key.toLowerCase().includes("tron") ||
        key.toLowerCase().includes("tronlink") ||
        key.includes("TRON")
      ) {
        localStorage.removeItem(key);
      }
    });

    console.log("✅ Tron storage cleaned up");
  } catch (error) {
    console.error("Error cleaning Tron storage:", error);
  }
};

/**
 * Cleans up all wallet storage (use on app start)
 * Removes all wallet-related storage except preserved fields
 */
export const cleanupAllWalletStorage = () => {
  try {
    // Get all localStorage keys
    const keys = Object.keys(localStorage);

    // Remove all wallet-related keys except preserved fields
    keys.forEach(key => {
      // Skip preserved fields
      if (PRESERVED_FIELDS.includes(key)) {
        return;
      }

      // Remove any wallet-related keys
      if (
        key.startsWith("wagmi.") ||
        key.startsWith("@w3m/") ||
        key.startsWith("wc@2:") ||
        key.startsWith("WALLET_CONNECT") ||
        key.toLowerCase().includes("walletconnect") ||
        key.toLowerCase().includes("wagmi") ||
        key.toLowerCase().includes("tron") ||
        key.toLowerCase().includes("tronlink") ||
        key.toLowerCase().includes("wallet") ||
        key.toLowerCase().includes("connect")
      ) {
        localStorage.removeItem(key);
      }
    });

    // Clear sessionStorage (but preserve important data if needed)
    const sessionKeys = Object.keys(sessionStorage);
    sessionKeys.forEach(key => {
      if (!PRESERVED_FIELDS.includes(key)) {
        sessionStorage.removeItem(key);
      }
    });

    // Clear cookies related to wallets
    document.cookie.split(';').forEach(cookie => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
      if (name.toLowerCase().includes('wallet') || name.toLowerCase().includes('connect')) {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      }
    });

    // IndexedDB cleanup
    if (typeof indexedDB !== 'undefined') {
      indexedDB.deleteDatabase("WALLET_CONNECT_V2_INDEXED_DB");
      indexedDB.deleteDatabase("W3M");
    }

    console.log("✅ All wallet storage cleaned up (preserved:", PRESERVED_FIELDS.join(', '), ")");
  } catch (error) {
    console.error("Error cleaning all wallet storage:", error);
  }
};

/**
 * Cleans up storage for specific platform type
 * Removes platform-specific storage except preserved fields
 */
export const cleanupStorageByPlatform = (platformType) => {
  if (platformType === 4) { // TRON_PLATFORM_TYPE
    cleanupTronStorage();
  } else {
    cleanupEvmStorage();
  }
};
